import { useEffect, useRef, useState } from "react";
import { FinalRound, Player } from "../types";
import "./JeopardyBoard.css";

const FINAL_SECONDS = 30;

const TEAM_COLORS = ['#1565c0', '#b71c1c', '#2e7d32'];

interface FinalJeopardyProps {
  final: FinalRound;
  onFinishGame: () => void;
  fjPhase?: string | null;
  fjAnswers?: (string | null)[];
  fjWagers?: (number | null)[];
  fjReviewIndex?: number;
  fjReviewShowWager?: boolean;
  fjReviewResults?: (string | null)[];
  players?: Player[];
}

function FinalJeopardy({
  final,
  fjPhase,
  fjAnswers = [null, null, null],
  fjWagers = [null, null, null],
  fjReviewIndex = 0,
  fjReviewShowWager = false,
  fjReviewResults = [null, null, null],
  players = [],
}: FinalJeopardyProps) {
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevFjPhase = useRef<string | null | undefined>(null);
  const themeRef = useRef<HTMLAudioElement | null>(null);

  // Start 30s countdown + the classic "Think!" theme when host triggers the
  // timer (fjPhase → 'thinking'). The 'question' phase is host-reading time
  // with no timer and no music.
  useEffect(() => {
    if (fjPhase === 'thinking' && prevFjPhase.current !== 'thinking') {
      setTimeLeft(FINAL_SECONDS);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setTimeLeft(t => {
          if (t === null || t <= 1) { clearInterval(timerRef.current!); return 0; }
          return t - 1;
        });
      }, 1000);
      if (themeRef.current) {
        themeRef.current.currentTime = 0;
        themeRef.current.volume = 0.6;
        themeRef.current.play().catch(() => {});
      }
    }
    // Stop timer + theme when leaving thinking phase
    if (fjPhase !== 'thinking' && prevFjPhase.current === 'thinking') {
      if (timerRef.current) clearInterval(timerRef.current);
      setTimeLeft(null);
      if (themeRef.current) {
        themeRef.current.pause();
        themeRef.current.currentTime = 0;
      }
    }
    prevFjPhase.current = fjPhase;
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fjPhase]);

  const showAnswer = fjPhase === 'answer';

  // The classic "Think!" theme — preloaded so it plays instantly when the
  // host reveals the question. Lives in public/sounds.
  const themeAudio = (
    <audio
      ref={themeRef}
      src="/sounds/jeopardy-theme.mp3"
      preload="auto"
    />
  );

  // Wagering screen
  if (!fjPhase || fjPhase === 'wagering') {
    return (
      <div className="clue" style={{ cursor: 'default' }}>
        <div className="clue-display final-category">{final.category}</div>
        <div className="fj-wagering-status">
          📝 Teams are placing their wagers…
        </div>
        {themeAudio}
      </div>
    );
  }

  // Per-team review screen — host walks through one team at a time, marks
  // right/wrong, then the wager flips into view (TV-game-show drama).
  if (fjPhase === 'review') {
    const idx = fjReviewIndex;
    const teamName = players[idx]?.name ?? `Team ${idx + 1}`;
    const teamColor = TEAM_COLORS[idx] ?? '#1565c0';
    const answer = fjAnswers[idx];
    const wager = fjWagers[idx];
    const result = fjReviewResults[idx];
    return (
      <div className="clue" style={{ cursor: 'default' }}>
        <div className="fj-review-wrap">
          <div className="fj-review-team" style={{ color: teamColor }}>
            {teamName}
          </div>
          <div className="fj-review-answer">
            {answer ? answer : <em style={{ opacity: 0.5 }}>(no answer)</em>}
          </div>
          {fjReviewShowWager && (
            <div className={`fj-review-verdict fj-review-verdict-${result}`}>
              {result === 'correct' ? '✓' : '✗'}
            </div>
          )}
          {fjReviewShowWager && (
            <div className="fj-review-wager">
              Wager: <strong>${(wager ?? 0).toLocaleString()}</strong>
            </div>
          )}
        </div>
        {themeAudio}
      </div>
    );
  }

  // Question / answer screen — no click or key handlers, host drives transitions
  return (
    <div className="clue" style={{ cursor: 'default' }}>
      <div className="clue-display">
        {final.html === true ? (
          <div dangerouslySetInnerHTML={{ __html: showAnswer ? final.solution : final.clue }} />
        ) : showAnswer ? (
          final.solution
        ) : (
          final.clue
        )}
      </div>
      {timeLeft !== null && !showAnswer && (
        <div className={`clue-bottom-timer ${timeLeft <= 10 ? 'clue-bottom-timer-urgent' : ''}`}>
          {timeLeft}
        </div>
      )}
      {themeAudio}
    </div>
  );
}

export default FinalJeopardy;
